/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHeader,
    IList,
    IOrderHistoryViewProps,
    IOrderInformation,
    ISalesOrder
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IGridSettings, IImageSettings, Image, IRequestContext } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { SalesLineState } from '@msdyn365-commerce-modules/order-management/src/common/sales-line-state';

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation> = ({
                                                                             orderInformationProps,
                                                                             salesId,
                                                                             receiptId,
                                                                             channelName,
                                                                             createdDate,
                                                                             count,
                                                                             amount,
                                                                             channelReferenceId
                                                                         }) => (
    <Node {...orderInformationProps}>
        {channelName}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
    </Node>
);

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
                                                                        deliveryProps,
                                                                        heading,
                                                                        count,
                                                                        processing,
                                                                        address,
                                                                        trackingInfo
                                                                    }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);
interface IGroup2 extends IGroup {
    moduleProps: any;
}
export const OrderHistoryGroup: React.FC<IGroup2> = ({ groupProps, delivery, salesLinesProps, salesLines, moduleProps }) => (
    <Node {...groupProps}>
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>
                        <SalesLineImageComponent moduleProps={moduleProps} data={salesLine.data} />
                    </React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);
interface IGroups2 extends IGroups {
    moduleProps: any;
}
export const OrderHistoryGroups: React.FC<IGroups2> = ({ groupsProps, groups, moduleProps }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderHistoryGroup key={index} {...group} moduleProps={moduleProps} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading, orderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);
interface ISalesOrder2 extends ISalesOrder {
    moduleProps: any;
}
export const OrderHistorySalesOder: React.FC<ISalesOrder2> = ({
                                                                  salesOrderProps,
                                                                  orderInfomation,
                                                                  groups,
                                                                  orderDetailsLink,
                                                                  expandProductsButton,
                                                                  moduleProps
                                                              }) => (
    <Node {...salesOrderProps}>
        {orderInfomation && <OrderHistoryOrderInfomation {...orderInfomation} />}
        {groups && <OrderHistoryGroups {...groups} moduleProps={...moduleProps} />}
        {expandProductsButton}
        {orderDetailsLink}
    </Node>
);

interface IList2 extends IList {
    moduleProps: any;
}

export const OrderHistoryList: React.FC<IList2> = ({ listProps, salesOrders, moduleProps }) => (
    <Node {...listProps}>
        {salesOrders &&
        salesOrders.map((salesOrder, index) => <OrderHistorySalesOder key={index} {...salesOrder} moduleProps={...moduleProps} />)}
    </Node>
);

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
                                                                orderHistoryProps,
                                                                header,
                                                                alert,
                                                                loading,
                                                                emptyMessage,
                                                                backToShoppingLink,
                                                                list,
                                                                table,
                                                                moreButton
                                                            }) => (
    <Module {...orderHistoryProps}>
        {header && <OrderHistoryHeader {...header} />}
        {loading}
        {alert && (
            <>
                {alert}
                {backToShoppingLink}
            </>
        )}
        {emptyMessage && (
            <Node className='ms-order-history__empty-message-container'>
                {emptyMessage}
                {backToShoppingLink}
            </Node>
        )}
        {list && <OrderHistoryList {...list} moduleProps={orderHistoryProps} />}
        {table}
        {moreButton && moreButton}
    </Module>
);

const SalesLineImageComponent: (props: any) => false | any = (props: any) => {
    const productName = props.data.product?.Name ?? props.data.salesLine.Description;
    const requestContext = props.moduleProps.moduleProps.context.actionContext.requestContext;
    const fallbackImageUrl = getFallbackImageUrl(props.data.product?.ItemId, requestContext.apiSettings);
    return getSalesLineImage(
        'ms-order-history',
        requestContext,
        productName,
        props.data.product,
        fallbackImageUrl,
        requestContext.gridSettings!,
        props.moduleProps.moduleProps.config.imageSettings,
        props.data.salesLineState,
        props.data.salesLine
    );
};

const getSalesLineImage = (
    moduleName: string,
    requestContext: IRequestContext,
    productName: string | undefined,
    product: SimpleProduct | undefined,
    fallbackImageUrl: string | undefined,
    gridSettings: IGridSettings,
    imageSettings: IImageSettings | undefined,
    salesLineState?: SalesLineState,
    salesLine?: any
) => {
    let productUrl = product?.PrimaryImageUrl ?? '';
    if (salesLine.ExtensionProperties && salesLine.ExtensionProperties.length > 0) {
        const imageUrl = salesLine.ExtensionProperties.find((e: any) => {
            return e.Key === 'AAA_RETAILIMAGEURL';
        });
        if (imageUrl && imageUrl.Value && imageUrl.Value.StringValue) {
            productUrl = imageUrl.Value.StringValue;
        }
    }
    return (
        !salesLineState?.isHidden && (
            <div className={`${moduleName}__sales-line-picture`}>
                <Image
                    requestContext={requestContext}
                    className={`${moduleName}__sales-line-image`}
                    altText={productName}
                    src={productUrl}
                    fallBackSrc={fallbackImageUrl}
                    gridSettings={gridSettings}
                    imageSettings={imageSettings}
                    loadFailureBehavior='empty'
                />
            </div>
        )
    );
};

export default OrderHistoryView;
